import React from "react";
import {RouteComponentProps} from "react-router-dom";
import {
    Button,
    Card,
    CardContent,
    Grid,
    Typography,
    Chip,
    CircularProgress,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    IconButton,
    Tooltip,
    Breadcrumbs,
    Link,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import ReceiptIcon from "@material-ui/icons/Receipt";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import WebhookIcon from "@material-ui/icons/Notifications";
import {AdminDashboardServiceSingleton, BrowserServiceSingleton} from "src/Context";
import {ReceiptLogDetail, TransactionStatus} from "../model/AdminDashboardTypes";

interface State {
    loading: boolean;
    error: string | null;
    detail: ReceiptLogDetail | null;
    copiedField: string | null;
}

const STATUS_CONFIG: Record<TransactionStatus, { bg: string; color: string; border: string; dotClass: string }> = {
    PAID: {bg: "#e8f5e8", color: "#28a745", border: "#c3e6c3", dotClass: "status-dot--success"},
    PROCESSING: {bg: "#fffbeb", color: "#b45309", border: "#fde68a", dotClass: "status-dot--warning status-dot--pulse"},
    FAILED: {bg: "#fef2f2", color: "#c62828", border: "#fecaca", dotClass: "status-dot--danger"},
    INITIATED: {bg: "#E8F4F8", color: "#55575A", border: "#d0e8f0", dotClass: "status-dot--muted"},
};

const STATUS_DISPLAY: Record<TransactionStatus, string> = {
    PAID: "Paid",
    PROCESSING: "Processing",
    FAILED: "Failed",
    INITIATED: "Initiated",
};

const LOAN_TYPE_LABELS: Record<string, string> = {
    APPRAISAL_FEE_LOAN: "Appraisal Fee Loan",
    INTEREST_LOAN: "Interest Loan",
};

export class ReceiptLogDetailView extends React.Component<RouteComponentProps<{ id: string }>, State> {
    private readonly dashboardService = AdminDashboardServiceSingleton;
    private readonly browserService = BrowserServiceSingleton;
    private copiedTimeout: ReturnType<typeof setTimeout> | null = null;

    state: State = {
        loading: false,
        error: null,
        detail: null,
        copiedField: null,
    };

    componentDidMount() {
        const id = Number(this.props.match.params.id);
        this.setState({loading: true});
        this.dashboardService
            .getReceiptLogDetail(id)
            .then((detail) => {
                this.setState({detail, loading: false});
            })
            .catch((err) => {
                this.setState({
                    error: err.message || "Failed to load receipt log detail",
                    loading: false,
                });
            });
    }

    componentWillUnmount() {
        if (this.copiedTimeout) clearTimeout(this.copiedTimeout);
    }

    /**
     * Back to the table. Opened from the table, this steps back in history, so the browser's own
     * Back button doesn't return here afterwards; the table restores its filters, page and scroll
     * either way. Opened directly (a pasted link), there is nothing to step back to.
     */
    private goBack = () => {
        const state = this.props.location.state as { fromTable?: boolean } | undefined;
        if (state && state.fromTable) {
            this.props.history.goBack();
        } else {
            this.browserService.redirectTo("/internal");
        }
    };

    /** A time to show, or a dash when there is none (new Date(null) would be 1 Jan 1970). */
    private formatDate = (iso: string | null | undefined): string => {
        if (!iso) return "—";
        return new Date(iso).toLocaleString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    };

    private formatCurrency = (amount: number | null): string => {
        if (amount === null) return "\u2014";
        return "$" + amount.toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    };

    private renderStatusBadge = (status: TransactionStatus, large?: boolean) => {
        const config = STATUS_CONFIG[status];
        return (
            <span
                className={`status-badge ${large ? "status-badge--lg" : ""}`}
                style={{
                    backgroundColor: config.bg,
                    color: config.color,
                    border: `1px solid ${config.border}`,
                }}
            >
                <span className={`status-dot ${config.dotClass}`} />
                {STATUS_DISPLAY[status]}
            </span>
        );
    };

    private copyToClipboard = (text: string, fieldName: string) => () => {
        navigator.clipboard.writeText(text).then(() => {
            this.setState({copiedField: fieldName});
            if (this.copiedTimeout) clearTimeout(this.copiedTimeout);
            this.copiedTimeout = setTimeout(() => this.setState({copiedField: null}), 2000);
        });
    };

    private renderField = (label: string, value: React.ReactNode, copyValue?: string) => (
        <Grid item xs={12} sm={6} md={4}>
            <div className="detail-field">
                <Typography variant="caption" color="textSecondary" className="detail-field__label">
                    {label}
                </Typography>
                <div className="detail-field__value-row">
                    <Typography variant="body1" className="detail-field__value">
                        {value || <span className="detail-field__empty">{"\u2014"}</span>}
                    </Typography>
                    {copyValue && (
                        <Tooltip title={this.state.copiedField === label ? "Copied!" : "Copy"}>
                            <IconButton size="small" onClick={this.copyToClipboard(copyValue, label)} className="detail-field__copy">
                                <FileCopyOutlinedIcon style={{fontSize: 14}} />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            </div>
        </Grid>
    );

    private renderReceiptSection = () => {
        const {detail} = this.state;
        if (!detail) return null;

        return (
            <Card className="detail-section" variant="outlined">
                <CardContent>
                    <div className="detail-section__header">
                        <ReceiptIcon className="detail-section__icon" />
                        <Typography variant="h6">Receipt Log</Typography>
                    </div>
                    <Grid container spacing={2}>
                        {this.renderField("ID", `#${detail.id}`)}
                        {this.renderField("Initiated Time", this.formatDate(detail.initiatedTime))}
                        {this.renderField("Payment Time", this.formatDate(detail.paymentTime))}
                        {this.renderField("Status",
                            this.renderStatusBadge(detail.transactionStatus)
                        )}
                        {this.renderField("Charge ID",
                            detail.chargeId
                                ? <span className="receipt-log-table__monospace">{detail.chargeId}</span>
                                : null,
                            detail.chargeId || undefined
                        )}
                        {this.renderField("Payment Intent ID",
                            detail.paymentIntentId
                                ? <span className="receipt-log-table__monospace">{detail.paymentIntentId}</span>
                                : null,
                            detail.paymentIntentId || undefined
                        )}
                        {this.renderField("Card Type", detail.cardType)}
                        {this.renderField("Last 4 Digits", detail.last4Digits ? `****${detail.last4Digits}` : null)}
                        {this.renderField("Name on Card", detail.nameOnCard)}
                        {this.renderField("Charge Type", detail.chargeType.length > 0 ? detail.chargeType.join(", ") : null)}
                        {this.renderField("Agreed to Terms",
                            <Chip
                                label={detail.agreedToTerms ? "Yes" : "No"}
                                size="small"
                                variant="outlined"
                                style={detail.agreedToTerms
                                    ? {color: "#2e7d32", borderColor: "#a5d6a7"}
                                    : {color: "#c62828", borderColor: "#ef9a9a"}
                                }
                            />
                        )}
                        {this.renderField("Receipt URL",
                            detail.receiptUrl ? (
                                <a href={detail.receiptUrl} target="_blank" rel="noopener noreferrer" className="detail-link">
                                    View Receipt
                                </a>
                            ) : null
                        )}
                    </Grid>
                </CardContent>
            </Card>
        );
    };

    private renderLoanSection = () => {
        const {detail} = this.state;
        if (!detail) return null;
        const loan = detail.loan;

        return (
            <Card className="detail-section" variant="outlined">
                <CardContent>
                    <div className="detail-section__header">
                        <AccountBalanceIcon className="detail-section__icon" />
                        <Typography variant="h6">Loan Information</Typography>
                        <Chip
                            label={LOAN_TYPE_LABELS[loan.loanType] || loan.loanType}
                            size="small"
                            variant="outlined"
                            style={{marginLeft: 12}}
                        />
                    </div>

                    <div className="detail-subsection">
                        <Typography variant="overline" className="detail-subsection__title">Core</Typography>
                        <Grid container spacing={2}>
                            {this.renderField("Loan Number", loan.loanNumber, loan.loanNumber)}
                            {this.renderField("Loan Type", LOAN_TYPE_LABELS[loan.loanType] || loan.loanType)}
                            {this.renderField("Loan Status", loan.loanStatus)}
                        </Grid>
                    </div>

                    <div className="detail-subsection">
                        <Typography variant="overline" className="detail-subsection__title">Borrower</Typography>
                        <Grid container spacing={2}>
                            {this.renderField("Full Name", loan.borrowerFullName)}
                            {this.renderField("Email",
                                loan.borrowerEmail
                                    ? <a href={"mailto:" + loan.borrowerEmail} className="detail-link">{loan.borrowerEmail}</a>
                                    : null
                            )}
                            {this.renderField("Phone",
                                loan.borrowerPhone
                                    ? <a href={"tel:" + loan.borrowerPhone} className="detail-link">{loan.borrowerPhone}</a>
                                    : null
                            )}
                        </Grid>
                    </div>

                    <div className="detail-subsection">
                        <Typography variant="overline" className="detail-subsection__title">Loan Officer</Typography>
                        <Grid container spacing={2}>
                            {this.renderField("Full Name", loan.loanOfficerFullName)}
                            {this.renderField("Email",
                                loan.loanOfficerEmail
                                    ? <a href={"mailto:" + loan.loanOfficerEmail} className="detail-link">{loan.loanOfficerEmail}</a>
                                    : null
                            )}
                            {this.renderField("Phone",
                                loan.loanOfficerPhone
                                    ? <a href={"tel:" + loan.loanOfficerPhone} className="detail-link">{loan.loanOfficerPhone}</a>
                                    : null
                            )}
                        </Grid>
                    </div>

                    <div className="detail-subsection">
                        <Typography variant="overline" className="detail-subsection__title">Property</Typography>
                        <Grid container spacing={2}>
                            {this.renderField("Address",
                                `${loan.propertyStreet}, ${loan.propertyCity}, ${loan.propertyState} ${loan.propertyZipCode}`
                            )}
                        </Grid>
                    </div>

                    <div className="detail-subsection">
                        <Typography variant="overline" className="detail-subsection__title">Dates</Typography>
                        <Grid container spacing={2}>
                            {this.renderField("Application Signed", loan.applicationSignedDate)}
                            {this.renderField("ITP Date", loan.itpDate)}
                        </Grid>
                    </div>

                    <div className="detail-subsection">
                        <Typography variant="overline" className="detail-subsection__title">Financials</Typography>
                        <Grid container spacing={2}>
                            {loan.loanType === "APPRAISAL_FEE_LOAN" && (
                                <>
                                    {this.renderField("Appraisal Fee", this.formatCurrency(loan.appraisalFee))}
                                    {this.renderField("Credit Report Fee", this.formatCurrency(loan.creditReportFee))}
                                    {this.renderField("Loan Amount", this.formatCurrency(loan.loanAmount))}
                                </>
                            )}
                            {loan.loanType === "INTEREST_LOAN" && (
                                <>
                                    {this.renderField("Payment Amount", this.formatCurrency(loan.paymentAmount))}
                                    {this.renderField("Payment Due", loan.paymentDue)}
                                    {this.renderField("Payment Due Investor", loan.paymentDueInvestor)}
                                </>
                            )}
                        </Grid>
                    </div>
                </CardContent>
            </Card>
        );
    };

    private renderWebhookSection = () => {
        const {detail} = this.state;
        if (!detail) return null;

        return (
            <Card className="detail-section" variant="outlined">
                <CardContent>
                    <div className="detail-section__header">
                        <WebhookIcon className="detail-section__icon" />
                        <Typography variant="h6">Stripe Webhook Events</Typography>
                        <Chip
                            label={detail.stripeWebhookEvents.length}
                            size="small"
                            style={{marginLeft: 8, minWidth: 28}}
                        />
                    </div>
                    {detail.stripeWebhookEvents.length === 0 ? (
                        <Typography variant="body2" color="textSecondary" style={{padding: "16px 0"}}>
                            No webhook events recorded for this transaction.
                        </Typography>
                    ) : (
                        <Paper variant="outlined" className="detail-webhook-table">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Event ID</TableCell>
                                        <TableCell>Event Type</TableCell>
                                        <TableCell>Payment Intent ID</TableCell>
                                        <TableCell>Processed At</TableCell>
                                        <TableCell>Created At</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {detail.stripeWebhookEvents.map((evt) => (
                                        <TableRow key={evt.id} hover>
                                            <TableCell className="receipt-log-table__monospace">{evt.eventId}</TableCell>
                                            <TableCell>
                                                <Chip label={evt.eventType} size="small" variant="outlined" />
                                            </TableCell>
                                            <TableCell className="receipt-log-table__monospace">
                                                {evt.paymentIntentId || "\u2014"}
                                            </TableCell>
                                            <TableCell className="receipt-log-table__nowrap">
                                                {this.formatDate(evt.processedAt)}
                                            </TableCell>
                                            <TableCell className="receipt-log-table__nowrap">
                                                {this.formatDate(evt.createdAt)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    )}
                </CardContent>
            </Card>
        );
    };

    render() {
        const {loading, error, detail} = this.state;

        if (loading) {
            return (
                <div className="receipt-log-detail__loading">
                    <CircularProgress />
                    <Typography variant="body2" color="textSecondary" style={{marginTop: 12}}>
                        Loading receipt details...
                    </Typography>
                </div>
            );
        }

        if (error) {
            return (
                <div className="receipt-log-detail">
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={this.goBack}
                    >
                        Back to Receipt Logs
                    </Button>
                    <div className="receipt-log-detail__error">
                        <Typography variant="body1" color="error">
                            {error}
                        </Typography>
                    </div>
                </div>
            );
        }

        if (!detail) return null;

        return (
            <div className="receipt-log-detail">
                <div className="receipt-log-detail__nav">
                    <Button
                        variant="text"
                        startIcon={<ArrowBackIcon />}
                        onClick={this.goBack}
                        size="small"
                    >
                        Back
                    </Button>
                    <Breadcrumbs separator=">" aria-label="breadcrumb">
                        <Link color="inherit" href="#" onClick={(e: React.MouseEvent) => { e.preventDefault(); this.goBack(); }}>
                            Receipt Logs
                        </Link>
                        <Typography color="textPrimary">#{detail.id}</Typography>
                    </Breadcrumbs>
                </div>

                <div className="receipt-log-detail__title-bar">
                    <Typography variant="h5">
                        Receipt #{detail.id}
                    </Typography>
                    {this.renderStatusBadge(detail.transactionStatus, true)}
                </div>

                {this.renderReceiptSection()}
                {this.renderLoanSection()}
                {this.renderWebhookSection()}
            </div>
        );
    }
}
